/**
 * WIM | 3D & AR — controlador del bloque "Ver en 3D".
 *
 * Sin dependencias. La PDP solo ejecuta este fichero: coloca el botón «Ver en 3D» sobre la
 * galería y, al pulsarlo, abre un diálogo con <model-viewer>. El visor (~280 KB gz), el
 * decodificador Draco y el modelo se cargan al abrir (o se precargan al ver la galería con buena
 * conexión). Nunca se precarga con ahorro de datos ni 2G/3G.
 *
 * Dispositivos:
 *   - Móvil/tablet: botón AR (Scene Viewer / Quick Look / WebXR). Sin soporte → aviso.
 *   - Escritorio: no hay AR posible; «Enviar al móvil» (Web Share o copiar enlace) abre la misma
 *     PDP con #ar y lanza la AR al cargar.
 * Cotas: fondo, ancho y alto medidos del modelo, proyectados con la cámara del visor.
 */
const SEL = '[data-wim-ar]';
const shared = { viewer: null };

const abs = (u) => { try { return new URL(u, location.href).href; } catch { return u; } };
const dirOf = (u) => { const a = abs(u).split('?')[0]; return a.slice(0, a.lastIndexOf('/') + 1); };
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
// Dispositivo con cámara y AR potencial (móvil/tablet). En escritorio se ofrece el traspaso al móvil.
const isMobileLike = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && matchMedia('(pointer: coarse)').matches);

function connectionOK() {
  const c = navigator.connection;
  if (!c) return true;
  if (c.saveData) return false;
  return !['slow-2g', '2g', '3g'].includes(c.effectiveType);
}

function loadViewer(cfg) {
  if (!shared.viewer) {
    // model-viewer lee self.ModelViewerElement al evaluarse: la ruta del decodificador Draco
    // (servido desde los assets del tema) debe fijarse ANTES del import.
    self.ModelViewerElement = Object.assign(self.ModelViewerElement || {}, { dracoDecoderLocation: dirOf(cfg.dracoBase) });
    shared.viewer = import(abs(cfg.viewerSrc)).then(() => {
      const MV = customElements.get('model-viewer');
      if (!MV) throw new Error('model-viewer no disponible');
      return MV;
    });
  }
  return shared.viewer;
}

function prefetch(url, as) {
  if (!url) return;
  const l = document.createElement('link');
  l.rel = 'prefetch';
  l.href = abs(url);
  if (as) l.as = as;
  document.head.appendChild(l);
}

function track(cfg, event, extra = {}) {
  if (cfg.gtm === false) return;
  const dl = (window.dataLayer = window.dataLayer || []);
  const it = cfg.item || {};
  dl.push({
    event,
    event_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    page_location: location.href,
    ar_source: cfg.source,
    ...extra,
    ecommerce: {
      currency: it.currency,
      value: it.price,
      items: [{
        item_id: it.item_id, item_group_id: it.item_group_id, item_name: it.item_name,
        item_variant: it.item_variant, item_brand: it.item_brand, item_category: it.item_category,
        item_sku: it.item_sku, price: it.price, quantity: 1,
      }],
    },
  });
}

/* Álgebra mínima para proyectar puntos 3D con la cámara de model-viewer */
const V = {
  sub: (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }),
  dot: (a, b) => a.x * b.x + a.y * b.y + a.z * b.z,
  cross: (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }),
  norm: (a) => { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; },
};

const CUBE_ICON = '<svg viewBox="0 0 18 21" fill="none" aria-hidden="true"><path d="M9 1 1.5 5.3v10.4L9 20l7.5-4.3V5.3L9 1Zm0 2.3 5.3 3L9 9.4 3.7 6.3 9 3.3ZM3.5 8l4.5 2.6v6.7L3.5 14.7V8Zm6.5 9.3v-6.7L14.5 8v6.7L10 17.3Z" fill="currentColor"/></svg>';

class WimAR {
  constructor(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector('[data-wim-ar-config]').textContent);
    this.dialog = root.querySelector('dialog');
    this.stage = root.querySelector('[data-wim-ar-stage]');
    this.hint = root.querySelector('[data-wim-ar-hint]');
    this.arBtn = root.querySelector('[data-wim-ar-launch]');
    this.handoff = root.querySelector('[data-wim-ar-handoff]');
    this.shareBtn = root.querySelector('[data-wim-ar-share]');
    this.dimToggle = root.querySelector('[data-wim-ar-dims]');
    this.mv = null;
    this.arActive = false;
    this.mobile = isMobileLike();
    root.classList.add(this.mobile ? 'is-mobile' : 'is-desktop');

    root.querySelectorAll('[data-wim-ar-open]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); this.open('block', b.hasAttribute('data-wim-ar-want-ar')); }));
    root.querySelector('[data-wim-ar-close]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('close', () => this.onClose());
    // Cinturón: en la PDP el evento 'close' no siempre llega al <dialog> (algo lo intercepta en captura);
    // la desaparición del atributo [open] no la puede parar nadie.
    new MutationObserver(() => { if (!this.dialog.open) this.onClose(); })
      .observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    this.arBtn.addEventListener('click', () => this.launchAR());
    if (this.shareBtn) this.shareBtn.addEventListener('click', () => this.share());
    if (this.dimToggle) this.dimToggle.addEventListener('click', () => this.toggleDimensions());

    this.mountPill();
    this.schedulePrefetch();

    // Deep link desde el traspaso de escritorio: #ar abre el visor y lanza la AR en cuanto carga.
    if (location.hash === '#ar' || location.hash === '#3d') {
      requestAnimationFrame(() => this.open('deeplink', location.hash === '#ar'));
    }

    // Editor de temas: seleccionar el bloque abre el diálogo para previsualizarlo.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => { if (e.target === root || e.target.contains(root)) this.open('editor'); });
      document.addEventListener('shopify:block:deselect', (e) => { if ((e.target === root || e.target.contains(root)) && this.dialog.open) this.dialog.close(); });
    }
  }

  /* Botón «Ver en 3D» sobre la galería. El tema usa layouts distintos según viewport (slider
     #x-product-{id} en móvil, grid #stacked-{id} en escritorio): se monta en el primer candidato
     visible y se recoloca al cambiar el tamaño de la ventana. */
  mountPill() {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'wim-ar__pill';
    pill.setAttribute('aria-haspopup', 'dialog');
    pill.setAttribute('aria-controls', this.dialog.id);
    pill.innerHTML = `${CUBE_ICON}<span>${this.cfg.labels.pill}</span>`;
    pill.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.open('gallery'); });
    // Fallback: la galería (Splide) puede tragarse el click si interpreta el gesto como arrastre.
    // Un toque sin desplazamiento (< 8 px) abre igualmente; open() ignora la llamada si ya está abierto.
    let down = null;
    pill.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY }; e.stopPropagation(); });
    pill.addEventListener('pointerup', (e) => {
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 8) setTimeout(() => { if (!this.dialog.open) this.open('gallery'); }, 60);
      down = null; e.stopPropagation();
    });
    this.pill = pill;
    this.placePill();
    let t;
    window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(() => this.placePill(), 150); }, { passive: true });
  }

  placePill() {
    const sid = this.cfg.sectionId;
    const visible = (el) => { if (!el) return false; const r = el.getBoundingClientRect(); return r.width > 40 && r.height > 40; };
    const candidates = [
      document.getElementById(`x-product-${sid}`),
      document.querySelector(`#stacked-${sid} .product-media-item.featured-image`),
      document.querySelector(`#stacked-${sid} .product-media-item`),
      document.querySelector(`[data-media-id^="${sid}-"]`),
    ];
    const target = candidates.find(visible);
    if (!target) { this.pill.remove(); return; }
    if (this.pill.parentElement === target) return;
    if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
    target.appendChild(this.pill);
  }

  /* Precarga silenciosa del visor y el modelo cuando la galería entra en pantalla */
  schedulePrefetch() {
    if (this.cfg.autoload === 'click' || !connectionOK()) return;
    const target = this.pill.parentElement || this.root;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      const run = () => { if (!connectionOK()) return; prefetch(this.cfg.viewerSrc, 'script'); prefetch(this.cfg.glb, 'fetch'); };
      'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 600);
    }, { rootMargin: '200px 0px' });
    io.observe(target);
  }

  async ensureViewer() {
    await loadViewer(this.cfg);
    if (this.mv) return this.mv;
    const c = this.cfg;
    const mv = document.createElement('model-viewer');
    const attrs = {
      src: abs(c.glb), alt: c.labels.alt,
      // Scene Viewer primero en Android (app nativa: mejor iluminación/oclusión que WebXR en el navegador)
      ar: '', 'ar-modes': 'scene-viewer webxr quick-look', 'ar-scale': 'fixed', 'ar-placement': 'floor',
      'camera-controls': '', 'touch-action': 'none',
      'shadow-intensity': '1', 'shadow-softness': '0.9', exposure: '0.85',
      'environment-image': 'neutral', 'tone-mapping': 'neutral',
      // Tres cuartos frontal: el frente del modelo (salida y bandeja) está en +X
      'camera-orbit': '60deg 78deg auto', 'min-camera-orbit': 'auto 55deg auto', 'max-camera-orbit': 'auto 100deg auto',
      'interaction-prompt': 'auto', loading: 'eager', reveal: 'auto',
    };
    if (c.usdz) attrs['ios-src'] = abs(c.usdz);
    if (c.poster) attrs.poster = abs(c.poster);
    if (!reducedMotion) Object.assign(attrs, { 'auto-rotate': '', 'auto-rotate-delay': '1200', 'rotation-per-second': '18deg' });
    for (const [k, v] of Object.entries(attrs)) mv.setAttribute(k, v);
    mv.className = 'wim-ar__viewer';
    // Ocultar el botón AR propio de model-viewer: el CTA es el del panel
    const arSlot = document.createElement('span'); arSlot.slot = 'ar-button'; arSlot.hidden = true; mv.appendChild(arSlot);
    mv.addEventListener('load', () => { this.root.classList.add('is-loaded'); this.settleAR(); this.setupDimensions(); });
    mv.addEventListener('error', () => { this.root.classList.add('is-error'); this.showHint(this.cfg.labels.loadError); });
    mv.addEventListener('ar-status', (e) => {
      const s = e.detail && e.detail.status;
      if (s === 'session-started' && !this.arActive) { this.arActive = true; track(this.cfg, 'view_ar', { ar_mode: mv.arMode || 'unknown' }); }
      if (s === 'not-presenting') this.arActive = false;
      if (s === 'failed') this.showHint(this.cfg.labels.arFailed);
    });
    this.stage.prepend(mv);
    this.mv = mv;
    return mv;
  }

  async open(origin, wantAR = false) {
    if (this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.classList.add('wim-ar-lock');
    track(this.cfg, 'view_3d', { ar_origin: origin });
    // En escritorio no hay AR posible: el panel ofrece el traspaso al móvil desde el principio
    if (!this.mobile) { this.root.classList.add('no-ar'); this.showHandoff(); }
    try {
      const mv = await this.ensureViewer();
      this.settleAR();
      if (this.root.classList.contains('show-dims')) this.startDimsLoop();
      if (wantAR && this.mobile) {
        const go = () => this.launchAR(true);
        mv.loaded ? go() : mv.addEventListener('load', go, { once: true });
      }
    } catch (err) {
      this.root.classList.add('is-error');
      this.showHint(this.cfg.labels.loadError);
    }
  }

  onClose() {
    if (!document.documentElement.classList.contains('wim-ar-lock')) return;
    document.documentElement.classList.remove('wim-ar-lock');
    clearInterval(this._arPoll);
    this.stopDimsLoop();
  }

  /* canActivateAR se resuelve de forma asíncrona tras el load: se espera hasta ~3 s antes de
     dar el móvil por no compatible. En escritorio siempre es no-ar. */
  settleAR() {
    if (!this.mv || !this.mobile) return;
    clearInterval(this._arPoll);
    const apply = () => { const can = !!this.mv.canActivateAR; this.root.classList.toggle('can-ar', can); this.root.classList.toggle('no-ar', !can); return can; };
    if (apply()) return;
    let tries = 0;
    this._arPoll = setInterval(() => {
      if (apply() || ++tries > 20) {
        clearInterval(this._arPoll);
        if (!this.mv.canActivateAR) this.showHint(this.cfg.labels.arUnsupported);
      }
    }, 150);
  }

  async launchAR(auto = false) {
    if (!this.mv) await this.ensureViewer();
    if (this.mv.canActivateAR) {
      try { await this.mv.activateAR(); }
      catch { if (auto) this.arBtn.classList.add('is-pulsing'); else this.showHint(this.cfg.labels.arFailed); }
      return;
    }
    if (!this.mobile) { this.showHandoff(); return; }
    this.showHint(this.cfg.labels.arUnsupported);
  }

  handoffUrl() {
    // En un tema no publicado el móvil debe aterrizar en el mismo preview, no en producción
    const th = window.Shopify && window.Shopify.theme;
    const preview = th && th.role !== 'main' && th.id ? `?preview_theme_id=${th.id}` : '';
    return `${location.origin}${location.pathname}${preview}#ar`;
  }

  showHandoff() {
    if (!this.handoff || !this.handoff.hidden) return;
    this.handoff.hidden = false;
    if (this.shareBtn) {
      this.shareBtn.hidden = !(navigator.share || navigator.clipboard);
      const label = this.shareBtn.querySelector('span');
      if (!navigator.share && label) label.textContent = this.cfg.labels.copyLink;
    }
    track(this.cfg, 'ar_handoff_view');
  }

  async share() {
    const url = this.handoffUrl();
    const it = this.cfg.item || {};
    if (navigator.share) {
      try {
        await navigator.share({ title: it.item_name, text: this.cfg.labels.shareText, url });
        track(this.cfg, 'ar_share', { method: 'web_share' });
        return;
      } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      this.flash(this.shareBtn, this.cfg.labels.copied);
      track(this.cfg, 'ar_share', { method: 'copy' });
    } catch { this.showHint(url); }
  }

  flash(btn, text) {
    if (!btn) return;
    const label = btn.querySelector('span');
    const prev = label ? label.textContent : btn.textContent;
    if (label) label.textContent = text; else btn.textContent = text;
    setTimeout(() => { if (label) label.textContent = prev; else btn.textContent = prev; }, 2200);
  }

  /* ---------- Cotas ---------- */

  /* Geometría de las cotas: las tres líneas (fondo en X, ancho en Z, alto en Y) salen de un
     único vértice ancla, la esquina inferior más cercana a la cámara, para que compartan origen.
     Se calcula una vez tras el load; la proyección se hace en cada fotograma. */
  setupDimensions() {
    if (this.dimGeo || this.cfg.showDims === false || !this.mv) return;
    const size = this.mv.getDimensions();
    const c = this.mv.getBoundingBoxCenter();
    if (!size || !size.x) return;
    const fmt = (m) => (m * 100).toLocaleString(document.documentElement.lang || 'es', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' cm';
    const L = this.cfg.labels;
    const note = this.cfg.heightNote ? ` · ${this.cfg.heightNote}` : '';
    /* La medida declarada en la ficha manda sobre la que mide la malla. El CAD incluye piezas que la
       ficha no cuenta — el pomo de la tapa son 1,5 cm —, así que el modelo da 37,0 cm de alto y la
       ficha 35,5. Mostrar el número del modelo contradice la ficha del producto. */
    const ov = this.cfg.dimsOverride || {};
    // La medida declarada se escribe con coma («40,5»); se formatea con el separador del idioma de la página
    const cm = (kind, meters) => { const v = parseFloat(String(ov[kind] || '').replace(',', '.')); return v > 0 ? fmt(v / 100) : fmt(meters); };
    /* Alto declarado en metros, para que la línea acabe donde dice la etiqueta y no en lo alto de la
       caja envolvente. Se ignora si no se parsea o si se aleja más de un 50% de lo que mide: un dedazo
       en el ajuste no debe dibujar una cota absurda. */
    const hM = parseFloat(String(ov.height || '').replace(',', '.')) / 100;
    const hyTop = (hM > size.y * 0.5 && hM < size.y * 1.5) ? -size.y / 2 + hM : size.y / 2;
    this.dimGeo = { c: { x: c.x, y: c.y, z: c.z }, size: { x: size.x, y: size.y, z: size.z }, hx: size.x / 2, hy: size.y / 2, hz: size.z / 2, hyTop };
    this.dimAnchor = null;

    // Capa propia sobre el visor: líneas SVG + etiquetas. No usa hotspots de model-viewer.
    const layer = document.createElement('div'); layer.className = 'wim-ar__dimlayer';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'wim-ar__dimlines'); svg.setAttribute('width', '100%'); svg.setAttribute('height', '100%');
    layer.appendChild(svg);
    const texts = { depth: `${L.depth} ${cm('depth', size.x)}`, width: `${L.width} ${cm('width', size.z)}`, height: `${L.height} ${cm('height', size.y)}${note}` };
    this.dimLines = {};
    for (const kind of ['depth', 'width', 'height']) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line'); line.setAttribute('class', 'wim-ar__dimline'); svg.appendChild(line);
      const el = document.createElement('span'); el.className = 'wim-ar__dim'; el.textContent = texts[kind]; layer.appendChild(el);
      this.dimLines[kind] = { line, el };
    }
    this.stage.appendChild(layer);
    this.dimLayer = layer;

    if (this.dimToggle) this.dimToggle.hidden = false;
    if (this.cfg.showDims !== false && !this.mobile) this.toggleDimensions(true);
  }

  /* Cámara real de three.js que model-viewer guarda en el elemento (propiedad simbólica: se
     localiza por forma, no por nombre). Con ella la proyección es la misma que la del fotograma,
     zoom y amortiguación incluidos. Si no se encuentra, se usa la fórmula analítica de abajo. */
  scene3() {
    if (this._scene3 !== undefined) return this._scene3;
    this._scene3 = null;
    for (const sym of Object.getOwnPropertySymbols(this.mv)) {
      const v = this.mv[sym];
      if (v && v.camera && v.camera.projectionMatrix && v.camera.matrixWorldInverse && v.target && v.target.matrixWorld) { this._scene3 = v; break; }
    }
    return this._scene3;
  }

  projector() {
    const mv = this.mv;
    const W = mv.clientWidth, H = mv.clientHeight;
    const sc = this.scene3();
    if (sc) {
      const M = sc.target.matrixWorld.elements, Vi = sc.camera.matrixWorldInverse.elements, Pm = sc.camera.projectionMatrix.elements;
      const mul = (m, x, y, z, w) => [m[0] * x + m[4] * y + m[8] * z + m[12] * w, m[1] * x + m[5] * y + m[9] * z + m[13] * w, m[2] * x + m[6] * y + m[10] * z + m[14] * w, m[3] * x + m[7] * y + m[11] * z + m[15] * w];
      const camPos = sc.camera.getWorldPosition ? sc.camera.getWorldPosition(sc.camera.position.clone()) : sc.camera.position;
      // posición de la cámara en coordenadas del modelo (inversa del grupo target, que es una traslación)
      const cam = { x: camPos.x - M[12], y: camPos.y - M[13], z: camPos.z - M[14] };
      return {
        cam,
        project(p) {
          const w = mul(M, p.x, p.y, p.z, 1);
          const v = mul(Vi, w[0], w[1], w[2], 1);
          if (v[2] >= 0) return null; // detrás de la cámara (three.js mira hacia -Z)
          const c = mul(Pm, v[0], v[1], v[2], 1);
          return { x: (c[0] / c[3] + 1) / 2 * W, y: (1 - c[1] / c[3]) / 2 * H };
        },
      };
    }
    // Respaldo analítico (convención three.js: theta desde +Z hacia +X, Y arriba; FOV vertical)
    const o = mv.getCameraOrbit(), t = mv.getCameraTarget();
    const aspect = W / H, tanH = Math.tan(mv.getFieldOfView() * Math.PI / 360);
    const cam = { x: t.x + o.radius * Math.sin(o.phi) * Math.sin(o.theta), y: t.y + o.radius * Math.cos(o.phi), z: t.z + o.radius * Math.sin(o.phi) * Math.cos(o.theta) };
    const f = V.norm(V.sub(t, cam)), r = V.norm(V.cross(f, { x: 0, y: 1, z: 0 })), u = V.cross(r, f);
    return {
      cam,
      project(p) {
        const d = V.sub(p, cam); const z = V.dot(d, f);
        if (z <= 1e-4) return null;
        return { x: (V.dot(d, r) / (z * tanH * aspect) + 1) / 2 * W, y: (1 - V.dot(d, u) / (z * tanH)) / 2 * H };
      },
    };
  }

  renderDimLines() {
    if (!this.mv || !this.dimGeo || !this.root.classList.contains('show-dims')) return;
    const pr = this.projector();
    const g = this.dimGeo, S = g.size;
    const P = (x, y, z) => ({ x: g.c.x + x, y: g.c.y + y, z: g.c.z + z });
    const hide = () => { for (const k in this.dimLines) { this.dimLines[k].line.classList.remove('is-on'); this.dimLines[k].el.classList.remove('is-on'); } };

    // Vértice ancla: la esquina inferior que se proyecta más abajo (la más cercana mirando desde
    // arriba). Histéresis de 8 px para no saltar entre dos esquinas casi a la misma altura.
    let best = null;
    for (const sx of [1, -1]) for (const sz of [1, -1]) {
      const q = pr.project(P(sx * g.hx, -g.hy, sz * g.hz));
      if (q && (!best || q.y > best.q.y)) best = { sx, sz, q };
    }
    if (!best) { hide(); return; }
    if (this.dimAnchor && (this.dimAnchor.sx !== best.sx || this.dimAnchor.sz !== best.sz)) {
      const prev = pr.project(P(this.dimAnchor.sx * g.hx, -g.hy, this.dimAnchor.sz * g.hz));
      if (prev && best.q.y - prev.y < 8) best = { sx: this.dimAnchor.sx, sz: this.dimAnchor.sz, q: prev };
    }
    this.dimAnchor = { sx: best.sx, sz: best.sz };
    const { sx, sz } = best;

    const ends = {
      depth: P(-sx * g.hx, -g.hy, sz * g.hz),
      width: P(sx * g.hx, -g.hy, -sz * g.hz),
      height: P(sx * g.hx, g.hyTop, sz * g.hz),
    };
    const labels = {
      depth: P(0, -g.hy - S.y * 0.07, sz * (g.hz + S.z * 0.15)),
      width: P(sx * (g.hx + S.x * 0.07), -g.hy - S.y * 0.07, 0),
      height: P(sx * g.hx, g.hyTop + S.y * 0.09, sz * g.hz),
    };
    for (const kind of ['depth', 'width', 'height']) {
      const { line, el } = this.dimLines[kind];
      const qb = pr.project(ends[kind]), ql = pr.project(labels[kind]);
      const on = !!(qb && ql);
      line.classList.toggle('is-on', on); el.classList.toggle('is-on', on);
      if (!on) continue;
      line.setAttribute('x1', best.q.x); line.setAttribute('y1', best.q.y);
      line.setAttribute('x2', qb.x); line.setAttribute('y2', qb.y);
      // La etiqueta se mantiene dentro del lienzo aunque su punto de anclaje quede fuera
      const W = this.mv.clientWidth, H = this.mv.clientHeight, pad = 18;
      const lx = Math.min(Math.max(ql.x, el.offsetWidth / 2 + pad), W - el.offsetWidth / 2 - pad);
      const ly = Math.min(Math.max(ql.y, pad), H - pad);
      el.style.transform = `translate(${lx}px, ${ly}px) translate(-50%, -50%)`;
    }
  }

  startDimsLoop() {
    this.stopDimsLoop();
    const tick = () => {
      if (!this.dialog.open || !this.root.classList.contains('show-dims')) { this.stopDimsLoop(); return; }
      this.renderDimLines();
      this._dimsRaf = requestAnimationFrame(tick);
    };
    this._dimsRaf = requestAnimationFrame(tick);
  }

  stopDimsLoop() {
    if (this._dimsRaf) cancelAnimationFrame(this._dimsRaf);
    this._dimsRaf = 0;
    if (this.dimLayer) this.dimLayer.querySelectorAll('.is-on').forEach((el) => el.classList.remove('is-on'));
  }

  toggleDimensions(force) {
    const on = typeof force === 'boolean' ? force : !this.root.classList.contains('show-dims');
    this.root.classList.toggle('show-dims', on);
    if (this.dimToggle) { this.dimToggle.hidden = false; this.dimToggle.setAttribute('aria-pressed', String(on)); }
    if (on && this.dialog.open && this.dimGeo) this.startDimsLoop(); else this.stopDimsLoop();
    if (typeof force !== 'boolean') track(this.cfg, 'view_3d_dimensions', { on });
  }

  showHint(msg) { if (!this.hint) return; this.hint.textContent = msg; this.hint.hidden = false; }
}

const init = () => document.querySelectorAll(SEL).forEach((r) => { if (!r.__wimAR) r.__wimAR = new WimAR(r); });
init();
// Secciones re-renderizadas por el editor de temas o cargadas después (quick view)
document.addEventListener('shopify:section:load', init);
